// AGC: FarmProHub Splash Screen Component
// AGC: Shows "Welcome to FARMPROHUB" on initial load

import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'

interface SplashProps {
  onComplete: () => void
  duration?: number // AGC: Duration in milliseconds (default: 3000ms)
  fullScreen?: boolean // AGC: If true, covers full screen
}

export const Splash: React.FC<SplashProps> = ({ onComplete, duration = 3000, fullScreen = false }) => {
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 375)
  const [isFading, setIsFading] = useState(false)

  // AGC: Track viewport width for full-width splash
  useEffect(() => {
    if (typeof window === 'undefined') return
    
    const updateWidth = () => setViewportWidth(window.innerWidth)
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  // AGC: Splash duration - configurable, then fade out and call onComplete
  useEffect(() => {
    const fadeStart = Math.max(0, duration - 500)
    const fadeTimer = setTimeout(() => setIsFading(true), fadeStart)
    const completeTimer = setTimeout(() => {
      onComplete()
    }, duration)

    return () => {
      clearTimeout(fadeTimer)
      clearTimeout(completeTimer)
    }
  }, [onComplete, duration])

  // AGC: Use full screen for app initialization, or respect nav bars
  const containerStyle = fullScreen
    ? {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        width: `${viewportWidth}px`,
        height: '100vh',
      }
    : {
        top: '60px',
        bottom: '64px',
        left: 0,
        right: 0,
        width: `${viewportWidth}px`,
        height: 'calc(100vh - 124px)',
      }

  const splashContent = (
    <div 
      className="fixed bg-gradient-to-br from-slate-950 via-[#003a5e] to-slate-950 flex flex-col items-center justify-center z-30" 
      style={{ 
        ...containerStyle,
        margin: 0,
        padding: 0,
        maxWidth: 'none',
        overflow: 'hidden',
        boxSizing: 'border-box',
        position: 'fixed',
        opacity: isFading ? 0 : 1,
        transition: 'opacity 0.5s ease-out',
      }}
    >
      {/* AGC: Animated Logo */}
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="mb-8"
      >
        <div className="w-32 h-32 bg-gradient-to-br from-[#0072CE] to-[#005a9e] rounded-3xl flex items-center justify-center shadow-2xl shadow-[#0072CE]/30">
          <span className="text-white font-bold text-5xl">FP</span>
        </div>
      </motion.div>

      {/* AGC: Welcome Text */}
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="text-center"
      >
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
          Welcome to
        </h1>
        <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-[#0072CE] to-[#0072CE] bg-clip-text text-transparent">
          FARMPROHUB
        </h2>
      </motion.div>

      {/* AGC: Tagline */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="mt-6 text-slate-400 text-sm sm:text-base"
      >
        Your Gateway to Base Blockchain
      </motion.p>

      {/* AGC: Loading indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.5 }}
        className="mt-12 flex items-center gap-2"
      >
        <div className="w-2 h-2 bg-[#0072CE] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 bg-[#0072CE] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 bg-[#0072CE] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </motion.div>
    </div>
  )

  // AGC: Portal to body for proper z-index handling
  if (typeof document !== 'undefined') {
    return createPortal(splashContent, document.body)
  }
  
  return splashContent
}

